import { useState } from 'react'
import { ActionIcon, Button, Group, Modal, Select, Stack, TextInput, Title, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { DataTable } from 'mantine-datatable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconPaperclip, IconPlus, IconSearch } from '@tabler/icons-react'
import {
  ESTADOS, esEditableEstado, type Requerimiento,
  type RequerimientoEntrada, type RequerimientoResumen,
} from '@serkat/shared'
import { ErrorApi, api, queryString } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { EstadoBadge } from '../../compartido/componentes/EstadoBadge'
import { PanelDocumento } from '../../compartido/componentes/PanelDocumento'
import { fecha } from '../../compartido/formato'
import { FormularioRequerimiento } from './componentes/FormularioRequerimiento'

const POR_PAGINA = 15

interface Pagina { datos: RequerimientoResumen[]; total: number }

export function RequerimientosPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [pagina, setPagina] = useState(1)
  const [texto, setTexto] = useState('')
  const [buscar] = useDebouncedValue(texto, 300)
  const [estado, setEstado] = useState<string | null>(null)
  const [editando, setEditando] = useState<Requerimiento | undefined>()
  const [abierto, modal] = useDisclosure(false)
  const [panel, setPanel] = useState<RequerimientoResumen | null>(null)

  const filtro = { pagina, porPagina: POR_PAGINA, buscar: buscar || undefined, estado: estado || undefined }

  const { data, isFetching } = useQuery({
    queryKey: ['requerimientos', filtro],
    queryFn: () => api.get<Pagina>(`/requerimientos${queryString(filtro)}`),
    placeholderData: (previo) => previo,
  })

  const guardar = useMutation({
    mutationFn: (v: RequerimientoEntrada) =>
      editando ? api.patch(`/requerimientos/${editando.id}`, v) : api.post('/requerimientos', v),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Requerimiento guardado' })
      void qc.invalidateQueries({ queryKey: ['requerimientos'] })
      modal.close()
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const puedeEmitir = usuario?.puedeEmitir ?? false

  async function abrir(fila?: RequerimientoResumen) {
    if (fila) setEditando(await api.get<Requerimiento>(`/requerimientos/${fila.id}`))
    else setEditando(undefined)
    modal.open()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Requerimientos</Title>
        {puedeEmitir && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
            Nuevo requerimiento
          </Button>
        )}
      </Group>

      <Group align="flex-end">
        <TextInput
          placeholder="Buscar por número, obra u observaciones"
          leftSection={<IconSearch size={16} />}
          value={texto}
          onChange={(e) => { setTexto(e.currentTarget.value); setPagina(1) }}
          w={360}
        />
        <Select
          placeholder="Todos los estados"
          data={ESTADOS.map((e) => ({ value: e, label: e }))}
          value={estado}
          onChange={(v) => { setEstado(v); setPagina(1) }}
          clearable
          w={190}
        />
      </Group>

      <DataTable
        withTableBorder borderRadius="sm" striped highlightOnHover minHeight={220}
        fetching={isFetching}
        records={data?.datos ?? []}
        totalRecords={data?.total ?? 0}
        recordsPerPage={POR_PAGINA}
        page={pagina}
        onPageChange={setPagina}
        noRecordsText="No hay requerimientos emitidos"
        idAccessor="id"
        columns={[
          {
            accessor: 'numero',
            title: 'Número',
            width: 150,
            render: (r) => <span style={{ fontFamily: 'monospace' }}>{r.numero}</span>,
          },
          { accessor: 'empresa', title: 'Emisor', width: '18%' },
          { accessor: 'fechaEmision', title: 'Emisión', width: 120, render: (r) => fecha(r.fechaEmision) },
          {
            accessor: 'fechaMaximaAtencion',
            title: 'Atender antes de',
            width: 140,
            render: (r) => fecha(r.fechaMaximaAtencion),
          },
          { accessor: 'obra', title: 'Obra', render: (r) => r.obra ?? '—' },
          { accessor: 'area', title: 'Área', width: 130, render: (r) => r.area ?? '—' },
          { accessor: 'totalItems', title: 'Ítems', width: 70, textAlign: 'center' },
          {
            accessor: 'estado',
            title: 'Estado',
            width: 120,
            render: (r) => <EstadoBadge estado={r.estado} />,
          },
          {
            accessor: 'acciones',
            title: '',
            width: 56,
            render: (r) => (
              <Group gap={2} wrap="nowrap">
                <Tooltip label="Adjuntos e historial">
                  <ActionIcon variant="subtle" onClick={() => setPanel(r)}>
                    <IconPaperclip size={16} />
                  </ActionIcon>
                </Tooltip>
                {puedeEmitir && esEditableEstado(r.estado) && (
                  <Tooltip label="Editar">
                    <ActionIcon variant="subtle" onClick={() => void abrir(r)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <PanelDocumento
        documentoId={panel?.id ?? null}
        numero={panel?.numero ?? ''}
        estado={panel?.estado ?? 'Pendiente'}
        puedeEditar={puedeEmitir}
        onCerrar={() => setPanel(null)}
      />

      <Modal
        opened={abierto}
        onClose={modal.close}
        title={editando ? `Editar ${editando.numero}` : 'Nuevo requerimiento'}
        size="90rem"
      >
        <FormularioRequerimiento
          inicial={editando}
          guardando={guardar.isPending}
          onGuardar={(v) => guardar.mutate(v)}
          onCancelar={modal.close}
        />
      </Modal>
    </Stack>
  )
}
